import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseUrlParamTabOptions<T extends string> {
  paramName: string;
  validValues: readonly T[];
  defaultValue: T;
  enabled?: boolean;
}

export const useUrlParamTab = <T extends string>({
  paramName,
  validValues,
  defaultValue,
  enabled = true,
}: UseUrlParamTabOptions<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo<T>(() => {
    if (!enabled) return defaultValue;
    const raw = searchParams.get(paramName);
    return (validValues as readonly string[]).includes(raw ?? '') ? (raw as T) : defaultValue;
  }, [enabled, searchParams, paramName, validValues, defaultValue]);

  const setActiveTab = useCallback(
    (value: T) => {
      if (!enabled) return;
      const next = new URLSearchParams(searchParams);
      next.set(paramName, value);
      setSearchParams(next, { replace: true });
    },
    [enabled, searchParams, paramName, setSearchParams],
  );

  return { activeTab, setActiveTab };
};
